import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type NotificationName =
    | 'auth-notification'
    | 'pwa-prompt'
    | 'push-subscription-prompt'
    | 'pwa-prompt-game'
    | 'error-message'
    | 'install-google-play-modal'
    | 'play-on-app-modal'

export type NotificationItem = {
    name: NotificationName
    priority: number
    onOpen: () => void
    onClose: () => void
}

export const useNotificationsQueue = defineStore('notification-queue', () => {
    // queue state
    const notificationsQueue = ref<NotificationItem[]>([])
    const activeName = ref<NotificationName | null>(null)
    const initialized = ref(false)
    // helpers
    const activeItem = computed<NotificationItem | null>(() =>
        activeName.value ? (notificationsQueue.value.find((n) => n.name === activeName.value) ?? null) : null,
    )

    function sortQueueDescByPriority() {
        notificationsQueue.value.sort((a, b) => b.priority - a.priority)
    }

    function existingItem(name: NotificationName) {
        return notificationsQueue.value.find((item) => item.name === name)
    }

    function dispatch() {
        if (!initialized.value || activeName.value || !notificationsQueue.value.length) {
            return
        }
        sortQueueDescByPriority()
        const next = notificationsQueue.value[0]
        const afterNext = notificationsQueue.value[1]

        if (!next) {
            return
        }

        activeName.value = next.name
        if (afterNext && next.priority > afterNext.priority) {
            forceCloseAndClearNotActive()
        }
        next.onOpen()
    }

    function remove(name: string) {
        const item = notificationsQueue.value.find((n) => n.name === name)
        item?.onClose()
        notificationsQueue.value = notificationsQueue.value.filter((n) => n.name !== name)
        if (activeName.value === name) {
            activeName.value = null
            dispatch()
        }
    }

    function forceCloseAndClearAll() {
        for (const n of notificationsQueue.value) {
            n.onClose()
        }
        notificationsQueue.value = []
        activeName.value = null
    }

    function dispose() {
        initialized.value = false
        forceCloseAndClearAll()
    }

    function forceCloseAndClearNotActive() {
        for (const n of notificationsQueue.value) {
            if (n.name !== activeName.value) {
                n.onClose()
            }
        }
        notificationsQueue.value = notificationsQueue.value.filter((n) => n.name === activeName.value)
    }

    function request(payload: NotificationItem) {
        if (activeItem.value && payload.priority < activeItem.value.priority) {
            return
        }
        if (activeItem.value && payload.priority > activeItem.value.priority) {
            forceCloseAndClearAll()
        }
        if (!existingItem(payload.name)) {
            notificationsQueue.value.push({
                name: payload.name,
                priority: payload.priority,
                onOpen: payload.onOpen,
                onClose: payload.onClose,
            })
        }
        dispatch()
    }

    function init() {
        initialized.value = true
        dispatch()
    }

    return {
        // state
        notificationsQueue,
        activeName,
        // api
        request,
        dispose,
        remove,
        init,
    }
})
